import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFeatureToggles } from './FeatureToggleProvider';
import { toast } from 'sonner';

/**
 * Redirects users away from disabled feature routes.
 * Handles exact matches AND child routes (e.g. /modules/:id maps to parent /modules).
 */
export default function FeatureRouteGuard({ children }: { children: React.ReactNode }) {
  const { isRouteEnabled, loading } = useFeatureToggles();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    const path = location.pathname;
    // Never guard root or app-settings itself
    if (path === '/' || path === '/app-settings') return;

    // Check exact match first
    if (!isRouteEnabled(path)) {
      toast.info('This feature is currently unavailable');
      navigate('/', { replace: true });
      return;
    }

    // Check parent route for child paths like /modules/abc123 or /modules/abc123/quiz
    // Split into segments and try progressively shorter prefixes
    const segments = path.split('/').filter(Boolean);
    if (segments.length > 1) {
      const parentPath = '/' + segments[0];
      if (!isRouteEnabled(parentPath)) {
        toast.info('This feature is currently unavailable');
        navigate('/', { replace: true });
        return;
      }
    }
  }, [location.pathname, isRouteEnabled, loading, navigate]);

  return <>{children}</>;
}
